import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { range } from 'lodash-es';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'topo-pager',
    templateUrl: './topo-table-pager.component.html',
    styleUrls: ['./topo-table-pager.component.scss'],
    standalone: false
})
export class TopoTablePagerComponent implements OnInit, OnChanges {
  @Input() skip = 0;
  @Input() take = 10;
  @Input() totalCount = 0;
  @Input() steps = 2;
  @Input() maxPagesForShowAll = 10;
  @Input() showChangeRowCount = false;
  @Input() hidePageLinks: boolean = false;
  @Output() paginate = new EventEmitter<number>();
  @Output() takeChanged = new EventEmitter<number>();

  totalPages: number;
  currentPage: number;
  pageLinks = [1] as number[];
  showingDisplay: string;

  constructor(private translate: TranslateService) {}

  ngOnInit(): void {
    this.generatePageLinks();
    this.generateDisplayCounts();
  }

  ngOnChanges(): void {
    this.generateDisplayCounts();
    this.generatePageLinks();
  }

  generateDisplayCounts(): void {
    if (this.totalCount <= this.skip && this.totalCount > 0) {
      this.skip = 0;
      this.paginate.emit(this.skip);
    }
    const fromValue = this.skip + 1;
    const toValue = this.skip + this.take > this.totalCount ? this.totalCount : this.skip + this.take;
    const totalValue = this.totalCount;

    this.translate
      .get('TABLE.PAGER.SHOWING', {
        fromValue,
        toValue,
        totalValue
      })
      .subscribe((res: string) => (this.showingDisplay = res));
  }

  generatePageLinks(): void {
    this.totalPages = Math.ceil(this.totalCount / this.take);
    this.currentPage = this.skip / this.take + 1;

    const stepsPlusOne = this.steps + 1;

    if (this.totalPages <= this.maxPagesForShowAll) {
      // range is inclusive of "from" param, but not the "to" param
      this.pageLinks = this.totalPages > 2 ? range(2, this.totalPages) : [];
    } else if (this.currentPage <= this.steps * 2) {
      // range is inclusive of "from" param, but not the "to" param
      this.pageLinks = range(2, this.steps * 2 + 3);
    } else if (this.currentPage >= this.totalPages - (this.steps * 2 - 1)) {
      // range is inclusive of "from" param, but not the "to" param
      this.pageLinks = range(this.totalPages - (this.steps * 2 + 1), this.totalPages);
    } else if (this.currentPage > stepsPlusOne) {
      // range is inclusive of "from" param, but not the "to" param
      this.pageLinks = range(this.currentPage - this.steps, this.currentPage);

      const tempTo = this.currentPage + stepsPlusOne;

      if (this.currentPage < this.totalPages) {
        this.pageLinks = [
          ...this.pageLinks,
          this.currentPage,
          ...range(this.currentPage + 1, tempTo >= this.totalPages ? this.totalPages : tempTo)
        ];
      }
    }
  }

  handlePageLink(page: number): void {
    if (page === 0) {
      this.skip = 0;
    } else if (page > 0) {
      this.skip = this.take * (page - 1);
    } else if (page === -1) {
      this.skip = this.take * (this.totalPages - 1);
    }

    this.paginate.emit(this.skip);
  }

  handlePreviousLink() {
    this.skip -= this.take;
    if (this.skip < 0) {
      this.skip = 0;
    }

    this.paginate.emit(this.skip);
  }

  handleNextLink() {
    this.skip += this.take;
    if (this.skip > this.totalCount) {
      this.skip = this.take * (this.totalPages - 1);
    }

    this.paginate.emit(this.skip);
  }

  isFirstPage(): boolean {
    return this.skip === 0;
  }

  isLastPage(): boolean {
    return this.skip + this.take >= this.totalCount;
  }

  changeRowsPerPage(rowCount: number): void {
    this.take = rowCount;

    this.takeChanged.emit(this.take);
  }
}
